import React, {useState, useEffect, useContext} from 'react';
import Input from 'epic-diesel-form/src/components/form/Input';
import Select from 'epic-diesel-form/src/components/form/Select';
import Languages from '../../../../constants/Languages';
import Countries, {states as States} from 'epic-diesel-form/src/constants/Countries';
import {useGetMessages} from '../../../../hooks';
import {ApplicationContext} from '../../../../hocs/WithApplicationView';
import {Form} from '../ApplicationForm';

//@ts-ignore
import styles from './RegionAndLanguageSection.pcss';

interface Props {
    form: Form;
    trigger: (k, v) => void;
    messages: Record<string, string>;
}

const RegionAndLanguageSection: React.FC<Props> = ({form, trigger, ...props}) => {
    const {schema} = useContext(ApplicationContext);
    const [options, setOptions] = useState(() => {
        return {
            languages: Languages,
            states: States,
            countries: Countries
        };
    });

    const messages = useGetMessages({
        header: 'epic.affiliate.signup.region.header',
        text: 'epic.affiliate.signup.region.text',
        textMinor: 'epic.affiliate.signup.region.text.minor.v2',
        languages: 'epic.affiliate.common.select_language',
        states: 'epic.affiliate.signup.location.state_or_province',
        countries: 'label.affiliate.form.tax.Country'
    });

    useEffect(() => {
        setOptions({
            languages: [{value: '', label: `${messages.languages} *`}].concat(Languages),
            states: [{value: '', label: `${messages.states} *`}].concat(States),
            countries: [{value: '', label: `${messages.countries} *`}].concat(Countries)
        });
    }, [messages]);

    const onChange = event => {
        const {name, value} = event.target;
        trigger(name, value);
    };

    const isForeign = form.country !== 'US';
    const isGuardian = form.guardianAcknowledgment === true;

    return (
        <>
            <h2>
                {messages.header} {' *'}
            </h2>
            <p>{isGuardian ? messages.textMinor : messages.text}</p>

            <div className={styles.fields}>
                <div className={styles.field}>
                    <Select
                        className={styles.languages}
                        name={schema.language.identifier}
                        placeholder={schema.language.display.text}
                        value={form.language}
                        onChange={trigger}
                        options={options.languages}
                        required
                    />
                </div>

                <div className={styles.field}>
                    <Select
                        name={schema.country.identifier}
                        value={form.country}
                        onChange={trigger}
                        options={options.countries}
                        validationErrors={schema.country.validationMessages}
                        required
                    />
                </div>

                <div className={styles.field}>
                    {isForeign ? (
                        <Input
                            name={schema.region.identifier}
                            value={form.state}
                            placeholder={schema.region.display.text}
                            validations={schema.region.validations}
                            validationErrors={schema.region.validationMessages}
                            onChange={onChange}
                            required
                        />
                    ) : (
                        <Select
                            name={schema.state.identifier}
                            value={form.state || ''}
                            placeholder={`${schema.state.display.text} *`}
                            validations={schema.state.validations}
                            validationErrors={schema.state.validationMessages}
                            onChange={trigger}
                            options={options.states}
                            required
                        />
                    )}
                </div>

                <div className={styles.field}>
                    <Input
                        name={schema.city.identifier}
                        value={form.city}
                        placeholder={schema.city.display.text}
                        validations={schema.city.validations}
                        validationErrors={schema.city.validationMessages}
                        onChange={onChange}
                        required
                    />
                </div>
            </div>
        </>
    );
};

export default React.memo(RegionAndLanguageSection);
